/**
 * Google Business Rating Input Component
 * Form fields for entering Google Business rating information
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Star, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';

export function GoogleRatingInput({ 
  googleMapsUrl, 
  setGoogleMapsUrl, 
  googleRating, 
  setGoogleRating, 
  googleReviewsCount, 
  setGoogleReviewsCount,
  isVerified = false,
  showVerificationStatus = true
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
      <div className="flex items-center gap-2 mb-3">
        <Star className="h-5 w-5 text-yellow-500" />
        <h3 className="font-semibold text-primary">{t('googleRating.title')}</h3>
        {showVerificationStatus && googleRating && (
          isVerified ? (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
              <CheckCircle className="h-3 w-3" />
              {t('googleRating.verified')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
              <AlertCircle className="h-3 w-3" />
              {t('googleRating.pendingVerification')}
            </span>
          )
        )}
      </div>
      
      <p className="text-sm text-secondary mb-4">{t('googleRating.description')}</p>
      
      {/* Google Maps URL */}
      <div>
        <label className="block text-sm font-medium text-primary mb-1">
          {t('googleRating.mapsUrl')}
        </label>
        <div className="relative">
          <Input
            type="url"
            placeholder={t('googleRating.mapsUrlPlaceholder')}
            value={googleMapsUrl || ''}
            onChange={(e) => setGoogleMapsUrl(e.target.value)}
            className="pr-10"
            data-testid="google-maps-url"
          />
          {googleMapsUrl && (
            <a 
              href={googleMapsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
        <p className="text-xs text-secondary mt-1">{t('googleRating.mapsUrlHint')}</p>
      </div>
      
      {/* Rating and Reviews Count in a row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-primary mb-1">
            {t('googleRating.rating')}
          </label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="5"
            placeholder={t('googleRating.ratingPlaceholder')}
            value={googleRating || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setGoogleRating(null);
              } else {
                const num = parseFloat(val);
                if (!isNaN(num) && num >= 0 && num <= 5) {
                  setGoogleRating(num);
                }
              }
            }}
            data-testid="google-rating"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-primary mb-1">
            {t('googleRating.reviewsCount')}
          </label>
          <Input
            type="number"
            min="0"
            placeholder={t('googleRating.reviewsCountPlaceholder')}
            value={googleReviewsCount || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setGoogleReviewsCount(null);
              } else {
                const num = parseInt(val, 10);
                if (!isNaN(num) && num >= 0) {
                  setGoogleReviewsCount(num);
                }
              }
            }}
            data-testid="google-reviews-count"
          />
        </div>
      </div>
    </div>
  );
}

export default GoogleRatingInput;
